import { useState, useRef } from 'react';
import { useStyletron, ThemeProvider } from 'baseui';
import { Button } from 'baseui/button';
import { Modal, ModalHeader, ModalBody, ModalFooter, ModalButton } from 'baseui/modal';

import { soundConfig } from '../../constants/display/soundConfig';
import { palettes } from '../../constants/display/palettes';
import { saveSoundEffect } from '../../services/db';
import { useNotification } from '../../contexts/NotificationContext';
import { version } from '../../version';

import {
    ModalContentWrapper,
    SettingsSectionContainer,
    createThemedModalOverrides,
    createDarkThemeForModal,
    HiddenFileInput,
    ThemeSelectionButton,
    ModalTitleText,
    SettingsHeading,
    ButtonWrapContainer,
    MenuGrid,
    MenuButton,
} from '../../styles/displaySettingsModalStyles';

import SoundSettingsTab from './SoundSettingsTab';
import TtsSettingsTab from './TtsSettingsTab';

export const DisplaySettingsModal = ({
    isOpen,
    onClose,
    settings,
    structure,
    actions,
    palette,
    t,
    isMobile,
    isPortrait,
    currentThemeName
}) => {
    const { playSound, setTheme } = actions;
    const { showNotification } = useNotification();
    const [, theme] = useStyletron();
    const [activeTabKey, setActiveTabKey] = useState("settingsTabTheme");

    const fileInputRef = useRef(null);
    const [isSoundSelectionModalOpen, setIsSoundSelectionModalOpen] = useState(false);
    const [soundSelectionContext, setSoundSelectionContext] = useState(null);
    const [selectedSoundFile, setSelectedSoundFile] = useState(null);
    const [selectedSoundFileNameForModal, setSelectedSoundFileNameForModal] = useState('');

    const [isTestingSound, setIsTestingSound] = useState(false);

    const handleTestSound = async (soundId, duration) => {
        if (isTestingSound) return;
        setIsTestingSound(true);
        await playSound(soundId, duration, () => {
            setIsTestingSound(false);
        }, true);
    };

    const handleSoundFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedSoundFile(file);
            setSelectedSoundFileNameForModal(file.name);
        }
    };

    const handleConfirmSoundSelection = async () => {
        if (selectedSoundFile && soundSelectionContext) {
            const { soundId } = soundConfig[soundSelectionContext];
            try {
                await saveSoundEffect(soundId, selectedSoundFile.name, selectedSoundFile);
                actions.handleSettingChange(soundSelectionContext, 'selectedSound', selectedSoundFile.name);
            } catch (error) {
                showNotification(t('errors.saveError'), 'error');
            }
        }
        setIsSoundSelectionModalOpen(false);
        setSelectedSoundFile(null);
    };

    if (!isOpen) return null;

    const tabsData = [
        { context: 'tournamentStart', titleKey: 'display.settingsTabTournamentStart' },
        { context: 'levelStart', titleKey: 'display.settingsTabLevelStart' },
        { context: 'breakStart', titleKey: 'display.settingsTabBreakStart' },
        { context: 'levelEndWarning', titleKey: 'display.settingsTabLevelEndWarning' },
    ];

    return (
        <>
            <Modal isOpen={isOpen} onClose={onClose} closeable animate autoFocus overrides={createThemedModalOverrides(palette, theme)}>
                <ThemeProvider theme={createDarkThemeForModal(palette)}>
                    <ModalHeader><ModalTitleText $palette={palette}>{t('common.settings')}</ModalTitleText></ModalHeader>
                    <ModalBody>
                        <MenuGrid>
                            <MenuButton
                                $active={activeTabKey === "settingsTabTheme"}
                                $palette={palette}
                                onClick={() => setActiveTabKey("settingsTabTheme")}
                            >
                                {t('display.settingsTabTheme')}
                            </MenuButton>
                            <MenuButton
                                $active={activeTabKey === "settingsTabTts"}
                                $palette={palette}
                                onClick={() => setActiveTabKey("settingsTabTts")}
                            >
                                {t('display.settingsTabTts') || '音声設定'}
                            </MenuButton>
                            {tabsData.map(tab => (
                                <MenuButton
                                    key={tab.context}
                                    $active={activeTabKey === tab.context}
                                    $palette={palette}
                                    onClick={() => setActiveTabKey(tab.context)}
                                >
                                    {t(tab.titleKey)}
                                </MenuButton>
                            ))}
                            <MenuButton
                                $active={activeTabKey === "credit"}
                                $palette={palette}
                                onClick={() => setActiveTabKey("credit")}
                            >
                                クレジット
                            </MenuButton>
                        </MenuGrid>

                        {activeTabKey === "settingsTabTheme" && (
                            <ModalContentWrapper>
                                <SettingsSectionContainer>
                                    <SettingsHeading $palette={palette}>{t('display.selectThemeLabel')}</SettingsHeading>
                                    <ButtonWrapContainer>
                                        {Object.keys(palettes).map(name => (
                                            <ThemeSelectionButton
                                                key={name}
                                                onClick={() => setTheme(name)}
                                                disabled={currentThemeName === name}
                                            >
                                                {t(`theme.name.${name}`) || name}
                                            </ThemeSelectionButton>
                                        ))}
                                    </ButtonWrapContainer>
                                </SettingsSectionContainer>
                            </ModalContentWrapper>
                        )}
                        {activeTabKey === "settingsTabTts" && (
                            <TtsSettingsTab
                                settings={settings}
                                actions={actions}
                                t={t}
                                palette={palette}
                            />
                        )}

                        {tabsData.map(tab => activeTabKey === tab.context && (
                            <SoundSettingsTab
                                key={tab.context}
                                category={tab.context}
                                settings={settings[tab.context]}
                                actions={actions}
                                t={t}
                                palette={palette}
                                structure={structure}
                                setSoundSelectionContext={setSoundSelectionContext}
                                setIsSoundSelectionModalOpen={setIsSoundSelectionModalOpen}
                                setSelectedSoundFileNameForModal={setSelectedSoundFileNameForModal}
                                isTestingSound={isTestingSound}
                                onTestSound={handleTestSound}
                            />
                        ))}

                        {activeTabKey === "credit" && (
                            <ModalContentWrapper>
                                <SettingsSectionContainer>
                                    <div style={{ color: palette.contentPrimary, textAlign: 'center', padding: '20px 0' }}>
                                        <h2 style={{ fontSize: '32px', margin: '0 0 10px 0', fontWeight: 'bold' }}>Blind Pace</h2>
                                        <p style={{ fontSize: '18px', margin: '0 0 5px 0', fontWeight: 'bold', opacity: 0.9 }}>v{version}</p>
                                        <p style={{ fontSize: '14px', margin: 0, opacity: 0.7 }}>©2026 Necron</p>
                                    </div>
                                </SettingsSectionContainer>
                            </ModalContentWrapper>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <ModalButton onClick={onClose}>{t('common.modalClose')}</ModalButton>
                    </ModalFooter>
                </ThemeProvider>
            </Modal>

            <Modal isOpen={isSoundSelectionModalOpen} onClose={() => setIsSoundSelectionModalOpen(false)} overrides={createThemedModalOverrides(palette, theme)}>
                <ThemeProvider theme={createDarkThemeForModal(palette)}>
                    <ModalHeader><ModalTitleText $palette={palette}>{t('display.selectSoundEffectButtonLabel')}</ModalTitleText></ModalHeader>
                    <ModalBody>
                        <Button onClick={() => fileInputRef.current?.click()}>{t('display.selectFileButtonLabel')}</Button>
                        <HiddenFileInput type="file" accept="audio/*" ref={fileInputRef} onChange={handleSoundFileChange} />
                        {selectedSoundFileNameForModal && <div style={{ color: palette.contentPrimary, marginTop: '10px' }}>{t('display.selectedFileLabelText')} {selectedSoundFileNameForModal}</div>}
                    </ModalBody>
                    <ModalFooter>
                        <ModalButton kind="tertiary" onClick={() => setIsSoundSelectionModalOpen(false)}>{t('common.modalCancel')}</ModalButton>
                        <ModalButton onClick={handleConfirmSoundSelection} disabled={!selectedSoundFile}>{t('display.confirmSelectionButtonLabel')}</ModalButton>
                    </ModalFooter>
                </ThemeProvider>
            </Modal>
        </>
    );
};
